import { apiService } from "@/lib/api";

const BASE_URL = "/api/v2/candidate-note";

const EMPTY_PAGINATION = { total: 0, page: 1, perPage: 10, totalPages: 0, paginated: false, hasNext: false, hasPrev: false };

/**
 * `PaginationData` extends `AppBaseModel`, whose alias generator is commented
 * out, so `response_model_by_alias=True` falls back to field names — a
 * snake_case block inside an otherwise camelCase body. Normalise it here, once,
 * so no component has to read `has_next ?? hasNext`.
 */
const normalizePagination = (pagination) => {
  if (!pagination) return { ...EMPTY_PAGINATION };
  return {
    total: pagination.total ?? 0,
    page: pagination.page ?? 1,
    perPage: pagination.per_page ?? pagination.perPage ?? EMPTY_PAGINATION.perPage,
    totalPages: pagination.total_pages ?? pagination.totalPages ?? 0,
    paginated: pagination.paginated ?? false,
    hasNext: pagination.has_next ?? pagination.hasNext ?? false,
    hasPrev: pagination.has_prev ?? pagination.hasPrev ?? false,
  };
};

/**
 * Collaborative notes on a candidate.
 *
 * The params and payloads here are camelCase, where most services in this app
 * send snake_case: every request model on this router extends
 * `AppBaseCamelCaseModel`, and the query params declare camel aliases
 * (`applicationId`, `perPage`). A snake key is simply ignored.
 */
export const CandidateNoteService = {
  /**
   * One page of the notes a tab shows, newest first.
   *
   * With `applicationId` the server answers with this candidate's own notes
   * plus that application's, and nothing from a sibling application. Leave it
   * unset for every note about the candidate — which is what a candidate opened
   * with no job gets. Passing it null omits it rather than sending the string
   * "null", which the UUID query parser answers with a 422.
   */
  async list(candidateId, { applicationId = null, page = 1, perPage = 10 } = {}) {
    const params = { page, perPage };
    if (applicationId) params.applicationId = applicationId;

    const response = await apiService.getSecure(`${BASE_URL}/candidate/${candidateId}`, { params });
    return { ...response.data, pagination: normalizePagination(response.data?.pagination) };
  },

  /**
   * `applicationId` is the scope: set, the note shows on that application only;
   * null, it shows on every application this candidate has.
   */
  async create({ candidateId, applicationId = null, body }) {
    const response = await apiService.postSecure(BASE_URL, { candidateId, applicationId, body });
    return response.data;
  },

  /**
   * Reword a note, and move it between scopes.
   *
   * `applicationId` is always sent, including as `null`. The server tells
   * "leave the scope alone" from "make this about the candidate" by whether the
   * key is present at all, so omitting it here would make the widening half of
   * the editor silently do nothing.
   */
  async update(noteId, { body, applicationId = null }) {
    const response = await apiService.patch(`${BASE_URL}/${noteId}`, { body, applicationId });
    return response.data;
  },

  async remove(noteId) {
    const response = await apiService.delete(`${BASE_URL}/${noteId}`);
    return response.data;
  },
};

export default CandidateNoteService;
